import { useTranslation } from 'react-i18next'
import EyeEnabled from '~/assets/icons/eye-enabled.svg?react'
import EyeDisabled from '~/assets/icons/eye-disabled.svg?react'

import { AdaptationIcon } from '~/components/icons/AdaptationIcon'
import { MenuSelect } from '~/components/MenuSelect'
import { Text } from '~/components/typography'
import Slider from '../Slider'

import {
  EROSION_BREAKS_M,
  EROSION_COLORMAP,
  EROSION_CONTEXT_COLORS,
  VisibleLayers,
  adaptationOptionsForSite,
  DEM_SCENARIOS,
  COSMOS_RETURN_PERIODS,
} from '~/constants'
import { useAppStore } from '~/hooks'
import { track } from '~/analytics'
import type { VisibleLayer } from '~/types/dataModel'

function swatchBackground(hex: string, alpha: number): string {
  const n = hex.replace('#', '')
  const r = parseInt(n.slice(0, 2), 16)
  const g = parseInt(n.slice(2, 4), 16)
  const b = parseInt(n.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha / 255})`
}

export function Erosion() {
  const { t } = useTranslation()
  const {
    toggleLayerVisibility,
    layerVisibility,
    setReturnPeriod,
    setDEM,
    setAdaptation,
    getCosmosScenario,
    DEM,
    adaptation,
    returnPeriod,
    cosmosSite,
  } = useAppStore()

  const formattedReturnPeriods = COSMOS_RETURN_PERIODS.map((rp) => {
    switch (rp) {
      case '005':
        return { label: t('cosmosReturnPeriods.5'), value: rp }
      case '020':
        return { label: t('cosmosReturnPeriods.20'), value: rp }
      case '050':
        return { label: t('cosmosReturnPeriods.50'), value: rp }
      case '100':
        return { label: t('cosmosReturnPeriods.100'), value: rp }
      default:
        return { label: rp, value: rp }
    }
  })

  const toggleLayer = (layer: VisibleLayer) => {
    const visible = !layerVisibility[layer]
    toggleLayerVisibility(layer)
    track('layer_visibility_toggled', { layer, visible })
  }

  const captureScenarioUpdate = (
    setting: 'return_period' | 'year' | 'adaptation',
    value: string
  ) => {
    track('scenario_updated', { setting, value })
  }

  const siteAdaptationOptions = adaptationOptionsForSite(cosmosSite)
  const formattedAdaptations = siteAdaptationOptions.map(({ value }) => ({
    label: t(`adaptation.${value}`),
    value,
  }))

  const formattedYear = DEM_SCENARIOS.map((dem) => {
    switch (dem) {
      case '020':
        return { label: t('cosmosYears.2025'), value: dem }
      case '050':
        return { label: t('cosmosYears.2055'), value: dem }
      case '080':
        return { label: t('cosmosYears.2085'), value: dem }
      default:
        return { label: dem, value: dem }
    }
  })

  const scenario = getCosmosScenario()
  const invalidScenario = DEM && adaptation && returnPeriod && !scenario

  const selectedAdaptationLabel =
    siteAdaptationOptions.find(({ value }) => value === adaptation) != null
      ? t(`adaptation.${adaptation ?? '000'}`)
      : t('adaptation.label')

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          {/* Centered over the color bar, so the arrow sits above the neutral (0) class. */}
          <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <Text className="text-right leading-tight text-neutral-midfi-grey" level={3}>
              {t('legend.erosionSide')}
              <br />
              (&lt;0 m)
            </Text>
            <Text className="text-neutral-midfi-grey" level={3}>
              →
            </Text>
            <Text className="text-left leading-tight text-neutral-midfi-grey" level={3}>
              {t('legend.depositionSide')}
              <br />
              (&gt;0 m)
            </Text>
            <button
              className="absolute right-0 top-1/2 -translate-y-1/2"
              onClick={() => toggleLayer(VisibleLayers.EROSION)}
            >
              {layerVisibility[VisibleLayers.EROSION] ? <EyeEnabled /> : <EyeDisabled />}
            </button>
          </div>
          <div className="flex w-full items-stretch gap-px">
            {EROSION_COLORMAP.map(({ hex, alpha }, i) => (
              <div
                key={`${hex}-${alpha}`}
                className="h-4 min-w-0 flex-1 border border-[#6F7983]/30"
                style={{
                  backgroundColor: swatchBackground(hex, alpha),
                  borderRadius:
                    i === 0
                      ? '4px 0 0 4px'
                      : i === EROSION_COLORMAP.length - 1
                        ? '0 4px 4px 0'
                        : '0',
                }}
              />
            ))}
          </div>
          <div className="relative h-4 w-full">
            {EROSION_BREAKS_M.map((edge, i) => (
              <span
                key={edge}
                className="absolute -translate-x-1/2 whitespace-nowrap text-xs text-neutral-midfi-grey"
                style={{ left: `${((i + 1) / EROSION_COLORMAP.length) * 100}%` }}
              >
                {/* The unit goes on the end ticks only, so the row stays uncluttered. */}
                {i === 0 || i === EROSION_BREAKS_M.length - 1 ? `${edge} m` : String(edge)}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5" viewBox="0 0 20 20">
              <line
                x1="2"
                y1="18"
                x2="18"
                y2="2"
                stroke={EROSION_CONTEXT_COLORS.road}
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <Text className="text-neutral-midfi-grey" level={3}>
              {t('legend.roads')}
            </Text>
          </div>
          <button onClick={() => toggleLayer(VisibleLayers.ROAD)}>
            {layerVisibility[VisibleLayers.ROAD] ? <EyeEnabled /> : <EyeDisabled />}
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="h-5 w-5 border"
              style={{
                backgroundColor: EROSION_CONTEXT_COLORS.building,
                borderColor: EROSION_CONTEXT_COLORS.buildingOutline,
              }}
            />
            <Text className="text-neutral-midfi-grey" level={3}>
              {t('legend.buildingsContext')}
            </Text>
          </div>
          <button onClick={() => toggleLayer(VisibleLayers.BUILDING)}>
            {layerVisibility[VisibleLayers.BUILDING] ? <EyeEnabled /> : <EyeDisabled />}
          </button>
        </div>

        {adaptation && adaptation !== '000' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AdaptationIcon adaptation={adaptation} />
              <Text className="text-neutral-midfi-grey" level={3}>
                {t('legend.adaptationLabel', { label: selectedAdaptationLabel })}
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggleLayer(VisibleLayers.ADAPTATION)}>
                {layerVisibility[VisibleLayers.ADAPTATION] ? <EyeEnabled /> : <EyeDisabled />}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-1.5 border-t border-neutral-midfi-grey/20 pt-3">
        <div className="space-y-4">
          <div className="flex flex-col items-center">
            <Text className="self-start text-neutral-midfi-grey" level={3}>
              {t('legend.returnPeriodYears')}
            </Text>
            <Slider
              options={formattedReturnPeriods}
              value={returnPeriod ?? '005'}
              onChange={(nextReturnPeriod) => {
                setReturnPeriod(nextReturnPeriod)
                captureScenarioUpdate('return_period', nextReturnPeriod)
              }}
              ariaLabel={t('legend.sliderReturnPeriod')}
            />
          </div>
          <div className="flex flex-col items-center">
            <Text className="self-start text-neutral-midfi-grey" level={3}>
              {t('legend.year')}
            </Text>
            <Slider
              options={formattedYear}
              value={DEM ?? '020'}
              onChange={(nextDEM) => {
                setDEM(nextDEM)
                captureScenarioUpdate('year', nextDEM)
              }}
              ariaLabel={t('legend.sliderYear')}
            />
          </div>
          <div className="space-y-1 pt-1 text-left">
            <Text className="text-neutral-midfi-grey" level={3}>
              {t('legend.adaptation')}
            </Text>
            <MenuSelect
              onChange={(option) => {
                setAdaptation(option.value)
                captureScenarioUpdate('adaptation', option.value)
              }}
              defaultValue={
                formattedAdaptations.find(({ value }) => value === (adaptation ?? '000')) ??
                formattedAdaptations[0]!
              }
              options={formattedAdaptations}
            />
          </div>
        </div>
      </div>

      {invalidScenario && (
        <Text className="mt-2 text-level-error" level={4}>
          {t('legend.invalidScenario')}
        </Text>
      )}
    </>
  )
}
